import React, { useState, useEffect, useCallback, useRef } from 'react';
import { itemFromProduct, applyVariant } from '../utils/orderItems';
import { productsApi, getErrorMessage } from '../utils/api';
import { isTicketItem, seatsLeft } from '../utils/tickets';
import { AlertTriangleIcon, ChevronRightIcon, PlusIcon } from './icons/LucideIcons';
import OrderFormSection from './OrderFormSection';
import ProductAutocomplete from './ProductAutocomplete';
import FormulaBuilder from './FormulaBuilder';
import { DEFAULT_PRODUCT_TYPE } from '../constants/productTypes';
import { matchProductType } from '../utils/keywordMatcher';
import SalesTaxNotSetChip from './SalesTaxNotSetChip';
import { staffOrderTax, formatTaxRate } from '../utils/salesTax';
import { quotedTax } from '../hooks/useStripeTaxQuote';

const COLOR_OPTIONS = [
  'Red',
  'Pink',
  'White',
  'Yellow',
  'Orange',
  'Purple',
  'Blue',
  'Green',
  'Mixed Colors',
  'Pastels',
  'Bright Colors',
  'No Preference',
];

const QUICK_TEMPLATES = {
  birthday: {
    name: 'Birthday Bouquet',
    arrangementType: 'bouquet',
    price: 45.0,
    colors: ['Mixed Colors', 'Bright Colors'],
    instructions: 'Bright, cheerful flowers perfect for birthday celebrations',
  },
  sympathy: {
    name: 'Sympathy Arrangement',
    arrangementType: 'vase',
    price: 65.0,
    colors: ['White', 'Pastels'],
    instructions: 'Elegant arrangement expressing condolences and sympathy',
  },
  romance: {
    name: 'Romantic Roses',
    arrangementType: 'bouquet',
    price: 75.0,
    colors: ['Red', 'Pink'],
    instructions: 'Beautiful roses perfect for romantic occasions',
  },
  getwell: {
    name: 'Get Well Soon',
    arrangementType: 'basket',
    price: 55.0,
    colors: ['Yellow', 'Orange', 'Mixed Colors'],
    instructions: "Uplifting arrangement to brighten someone's day",
  },
};

const ItemsSection = ({
  items,
  onItemsChange,
  validationErrors = {},
  onNotification,
  deliveryFee = 0,
  priorityTimeFee = 0,
  salesTaxRate = 0, // The shop's rate; 0 until it is set
  salesTaxRateNotSet = false, // shows the "Sales tax rate not set" chip
  // Stripe Tax mode: the server's quote (hooks/useStripeTaxQuote); the tax
  // shows "Calculating…" until it answers. null: flat mode, taxed here.
  taxQuote = null,
  isTaxExempt = false,
  onTaxExemptChange,
  showLowStockWarnings = true, // Show low inventory warnings
  tabIndex = 30,
  showTotals = true, // false when the page renders its own totals
  // 'forest': the New order form's Items step, rendered as its own numbered
  // section card (`step`). Edit order keeps the original layout.
  layout,
  step,
}) => {
  const [showProductModal, setShowProductModal] = useState(false);
  const [products, setProducts] = useState([]);
  const stripeTaxMode = taxQuote != null;
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [expandedColorPrefs, setExpandedColorPrefs] = useState({});
  const [placeholderProducts, setPlaceholderProducts] = useState([]);
  // Track which items have had their productType manually changed by the user
  const [manuallySetTypes, setManuallySetTypes] = useState({});
  const [formulaItemIndex, setFormulaItemIndex] = useState(null);

  // Fetch placeholder products on mount
  useEffect(() => {
    const fetchPlaceholders = async () => {
      try {
        const response = await productsApi.getPlaceholders();
        if (response.success && response.data?.placeholders) {
          setPlaceholderProducts(response.data.placeholders);
        }
      } catch (error) {
        console.error('Failed to fetch placeholder products:', error);
      }
    };
    fetchPlaceholders();
  }, []);

  // Build list of product type options from placeholders (with fallback to constants)
  const productTypeOptions = placeholderProducts.length > 0
    ? placeholderProducts.map(p => p.title)
    : [DEFAULT_PRODUCT_TYPE];

  // Get the placeholder product's variant ID for a given product type title
  const getPlaceholderVariantId = useCallback((productTypeTitle) => {
    const placeholder = placeholderProducts.find(p => p.title === productTypeTitle);
    return placeholder?.shopify_variant_id || null;
  }, [placeholderProducts]);

  // Calculate running total
  const calculateTotal = () => {
    return items.reduce((total, item) => {
      const price = parseFloat(item.price) || 0;
      const quantity = parseInt(item.quantity) || 0;
      return total + price * quantity;
    }, 0);
  };

  // The order's tax in dollars: Stripe Tax mode, the server's (null until it
  // answers); flat mode, the shop's rate on the lines.
  const orderTax = () => (stripeTaxMode
    ? quotedTax(taxQuote, isTaxExempt)
    : staffOrderTax({ itemsSubtotal: calculateTotal(), deliveryFee, priorityTimeFee, rate: salesTaxRate, exempt: isTaxExempt }));
  const taxRateShown = () => (stripeTaxMode ? taxQuote.rate : salesTaxRate);
  const taxNotYet = () => (taxQuote && taxQuote.status === 'failed' ? '—' : 'Calculating…');

  // Format currency
  const formatCurrency = (value) => {
    if (!value && value !== 0) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  // Parse currency input
  const parseCurrency = (value) => {
    if (!value) return '';
    // Remove currency symbols and parse as float
    const cleaned = value.replace(/[$,]/g, '');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? '' : parsed.toFixed(2);
  };

  // Handle item changes
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };

    // Auto-detect product type from description for custom items
    if (field === 'name' && !updatedItems[index].isProduct) {
      const itemId = updatedItems[index].id;
      // Only auto-update if user hasn't manually changed from default
      if (!manuallySetTypes[itemId]) {
        const detectedType = matchProductType(value, productTypeOptions);
        if (detectedType) {
          updatedItems[index].productType = detectedType;
          updatedItems[index].placeholderVariantId = getPlaceholderVariantId(detectedType);
        } else {
          updatedItems[index].productType = DEFAULT_PRODUCT_TYPE;
          updatedItems[index].placeholderVariantId = getPlaceholderVariantId(DEFAULT_PRODUCT_TYPE);
        }
      }
    }

    // When productType is manually changed, store the variant ID and mark as manual
    if (field === 'productType') {
      updatedItems[index].placeholderVariantId = getPlaceholderVariantId(value);
      setManuallySetTypes(prev => ({ ...prev, [updatedItems[index].id]: true }));
    }

    onItemsChange(updatedItems);
  };

  // Latest items, for async updates that land after the user has moved on
  const itemsRef = useRef(items);
  itemsRef.current = items;

  // Ticket products: the synced stock is hours old, so read seats left per
  // slot live from Shopify and fold them into the item's variants.
  const loadLiveSeats = async (itemId, variants) => {
    const ids = variants.map(v => v.id).filter(Boolean);
    if (ids.length === 0) return;
    try {
      const response = await productsApi.getVariantInventory(ids);
      const live = new Map((response.data?.variants || []).map(v => [v.id, v]));
      const merge = (v) => {
        const l = v && live.get(v.id);
        return l ? { ...v, inventoryQuantity: l.inventoryQuantity, inventoryTracked: l.tracked, liveInventory: true } : v;
      };
      onItemsChange(itemsRef.current.map(item => item.id !== itemId ? item : {
        ...item,
        variants: (item.variants || []).map(merge),
        ...(item.selectedVariant && { selectedVariant: merge(item.selectedVariant) }),
      }));
    } catch (error) {
      console.error('Failed to load live seat counts:', error);
      onNotification?.("Couldn't load seats left from Shopify. Check the count there before selling.", 'warning', 6000);
    }
  };

  // Handle product selection from autocomplete
  const handleProductSelect = (index, product) => {
    const updatedItems = [...items];
    // Shopify products keep their variant GIDs in shopify_variant_id; native
    // products (Online store) carry a variant_id row and, with several size
    // tiers, the same picker. See utils/orderItems.js.
    updatedItems[index] = itemFromProduct(updatedItems[index], product);
    const hasMultipleVariants = updatedItems[index].needsVariantSelection;
    onItemsChange(updatedItems);

    if (product.is_shopify_synced && isTicketItem(updatedItems[index])) {
      loadLiveSeats(updatedItems[index].id, product.variants || []);
    }

    if (hasMultipleVariants) {
      onNotification?.(
        `✓ Added ${product.title} - Please select a variant`,
        'info'
      );
    } else {
      // Show low stock warning if applicable and enabled
      if (showLowStockWarnings && product.track_inventory && product.inventory_quantity !== null && product.inventory_quantity < 5) {
        onNotification?.(
          `⚠️ Low Stock: Only ${product.inventory_quantity} of "${product.title}" available`,
          'warning'
        );
      } else {
        // Show success notification
        onNotification?.(
          `✓ Added ${product.title} - $${parseFloat(product.price || 0).toFixed(2)}`,
          'success'
        );
      }
    }
  };

  // Handle extra text typed after a selected product name
  // Moves the extra text into special instructions and restores the product name
  const handleBlurExtract = (index, extraText) => {
    const updatedItems = [...items];
    const item = updatedItems[index];
    const existing = (item.instructions || '').trim();
    updatedItems[index] = {
      ...item,
      name: item.selectedProductTitle,
      instructions: existing ? `${existing}; ${extraText}` : extraText,
    };
    onItemsChange(updatedItems);
    onNotification?.(`Moved "${extraText}" to special instructions`, 'info', 3000);
  };

  // Handle variant selection
  const handleVariantSelect = (index, variant) => {
    const updatedItems = [...items];
    // Name gets the option as a suffix for work tickets; the id lands in
    // shopify_variant_id (Shopify) or variant_id (native), never both.
    updatedItems[index] = applyVariant(updatedItems[index], variant);
    onItemsChange(updatedItems);

    onNotification?.(
      `✓ Selected ${variant.title} - $${parseFloat(variant.price || 0).toFixed(2)}`,
      'success',
      2000
    );
  };

  // Add new item
  const addItem = (type = 'custom', template = null) => {
    const defaultVariantId = getPlaceholderVariantId(DEFAULT_PRODUCT_TYPE);
    const newItem = template
      ? {
          id: Date.now(),
          type: 'custom',
          isProduct: false,
          name: template.name,
          price: template.price.toFixed(2),
          quantity: 1,
          arrangementType: template.arrangementType,
          colors: template.colors,
          instructions: template.instructions,
          productType: DEFAULT_PRODUCT_TYPE,
          placeholderVariantId: defaultVariantId,
        }
      : {
          id: Date.now(),
          type: type,
          isProduct: type === 'product',
          name: '',
          price: '',
          quantity: 1,
          arrangementType: '',
          colors: [],
          instructions: '',
          productId: null,
          variantId: null,
          productType: DEFAULT_PRODUCT_TYPE,
          placeholderVariantId: defaultVariantId,
        };

    onItemsChange([...items, newItem]);

    if (template) {
      onNotification?.(`Added ${template.name} template`, 'success', 2000);
    }
  };

  // Remove item
  const removeItem = (index) => {
    if (items.length === 1) {
      onNotification?.('Cannot remove the last item', 'warning');
      return;
    }

    const updatedItems = items.filter((_, i) => i !== index);
    onItemsChange(updatedItems);
    onNotification?.('Item removed', 'info', 2000);
  };

  // Duplicate item
  const duplicateItem = (index) => {
    const itemToDuplicate = { ...items[index] };
    itemToDuplicate.id = Date.now();
    itemToDuplicate.name = `${itemToDuplicate.name} (Copy)`;

    const updatedItems = [...items];
    updatedItems.splice(index + 1, 0, itemToDuplicate);
    onItemsChange(updatedItems);
    onNotification?.('Item duplicated', 'success', 2000);
  };

  // Clear item - reset to blank state
  const clearItem = (index) => {
    const newId = Date.now();
    const updatedItems = [...items];
    updatedItems[index] = {
      id: newId, // New ID to force re-render
      type: 'custom',
      isProduct: false,
      name: '',
      price: '',
      originalPrice: null,
      priceNote: '',
      quantity: 1,
      arrangementType: '',
      colors: [],
      instructions: '',
      productId: null,
      variantId: null,
      selectedProductTitle: null,
      shopify_product_id: null,
      shopify_variant_id: null,
      image_url: null,
      variants: [],
      inventoryQuantity: null,
      inventoryTracked: false,
      productType: DEFAULT_PRODUCT_TYPE,
      placeholderVariantId: getPlaceholderVariantId(DEFAULT_PRODUCT_TYPE),
    };
    // Clear manual type flag for this item
    setManuallySetTypes(prev => {
      const updated = { ...prev };
      delete updated[items[index].id];
      return updated;
    });
    onItemsChange(updatedItems);
    onNotification?.('Item cleared', 'info', 2000);
  };

  // Apply a formula built in the Formula Calculator to an existing item.
  // Keeps productType / placeholderVariantId so work-ticket tracking stays intact.
  //
  // For an item that was already on a saved order (flagged existingLineItem
  // by OrderEditForm) AND already carries a non-zero price, we keep the
  // existing price + quantity — the formula only contributes name and
  // breakdown. Prevents the calculator from silently undercharging when
  // staff use it to compose a formula for an item the customer was already
  // quoted on.
  const applyFormulaToItem = (index, formula) => {
    const current = items[index];
    const currentPrice = Number(current.price);
    const preservePrice =
      current.existingLineItem === true && currentPrice > 0;

    const updatedItems = [...items];
    updatedItems[index] = {
      ...current,
      name: formula.name,
      price: preservePrice ? current.price : formula.price,
      quantity: preservePrice ? current.quantity : formula.quantity,
      instructions: formula.instructions,
      arrangementType: current.arrangementType || 'vase',
    };
    onItemsChange(updatedItems);
    setFormulaItemIndex(null);
    if (preservePrice) {
      onNotification?.(
        `Formula added · existing ${formatCurrency(currentPrice)} price kept`,
        'info',
        3500
      );
    } else {
      onNotification?.('Formula added to item', 'success', 2000);
    }
  };

  // Apply template (for future template UI)
  // eslint-disable-next-line no-unused-vars
  const applyTemplate = (templateKey) => {
    const template = QUICK_TEMPLATES[templateKey];
    addItem('custom', template);
  };

  // Load products
  const loadProducts = async () => {
    try {
      setLoadingProducts(true);
      const response = await productsApi.getAll(50);
      if (response.success) {
        setProducts(response.data);
        setShowProductModal(true);
      }
    } catch (error) {
      console.error('Failed to load products:', error);
      onNotification?.(getErrorMessage(error), 'error');
    } finally {
      setLoadingProducts(false);
    }
  };

  // Select product
  const selectProduct = (product, variant = null) => {
    const itemPrice = variant ? variant.price : product.variants[0]?.price || '0.00';
    const productItem = {
      id: Date.now(),
      type: 'product',
      isProduct: true,
      name: variant ? `${product.title} - ${variant.title}` : product.title,
      price: itemPrice,
      originalPrice: itemPrice,
      priceNote: '',
      quantity: 1,
      productId: product.id,
      shopify_product_id: product.shopify_id,
      shopify_variant_id: product.shopify_variant_id, // Shopify GID for draft orders
      variantId: variant ? variant.id : product.variants[0]?.id,
      image: product.images?.[0]?.src || null,
      instructions: '',
      tags: product.tags || [], // Forwarded for campaign eligibility checks
    };

    onItemsChange([...items, productItem]);
    setShowProductModal(false);
    onNotification?.(`Added ${productItem.name}`, 'success', 2000);
  };

  // Handle color selection
  const handleColorChange = (itemIndex, color, checked) => {
    const updatedItems = [...items];
    const currentColors = updatedItems[itemIndex].colors || [];

    if (checked) {
      updatedItems[itemIndex].colors = [...currentColors, color];
    } else {
      updatedItems[itemIndex].colors = currentColors.filter((c) => c !== color);
    }

    onItemsChange(updatedItems);
  };

  // Toggle color preferences accordion
  const toggleColorPrefs = (itemId) => {
    setExpandedColorPrefs(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const getFieldError = (itemIndex, field) =>
    validationErrors[`items.${itemIndex}.${field}`];

  // Product Modal Component
  const ProductModal = () => {
    if (!showProductModal) return null;

    return (
      <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
        <div className='bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-y-auto'>
          <div className='p-6'>
            <div className='flex justify-between items-center mb-4'>
              <h3 className='text-lg font-semibold text-floral-800'>
                Select Product
              </h3>
              <button
                onClick={() => setShowProductModal(false)}
                className='text-gray-400 hover:text-gray-600'
              >
                <svg
                  className='w-6 h-6'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>

            {loadingProducts ? (
              <div className='flex justify-center items-center py-8'>
                <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-floral-600'></div>
                <p className='ml-3 text-floral-600'>Loading products...</p>
              </div>
            ) : (
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
                {products.map((product) => (
                  <div
                    key={product.id}
                    className='border border-floral-200 rounded-lg p-4 hover:border-floral-400 transition-colors'
                  >
                    {product.images && product.images[0] && (
                      <img
                        src={product.images[0].src}
                        alt={product.title}
                        className='w-full h-40 object-cover rounded mb-3'
                      />
                    )}
                    <h4 className='font-semibold text-floral-800 mb-2'>
                      {product.title}
                    </h4>
                    <p className='text-sm text-floral-600 mb-3 line-clamp-2'>
                      {product.body_html?.replace(/<[^>]*>/g, '') ||
                        'No description available'}
                    </p>

                    {product.variants && product.variants.length > 1 ? (
                      <div className='space-y-2'>
                        {product.variants.map((variant) => (
                          <button
                            key={variant.id}
                            onClick={() => selectProduct(product, variant)}
                            className='w-full flex justify-between items-center p-2 border border-floral-200 rounded hover:bg-floral-50 text-sm'
                          >
                            <span>{variant.title}</span>
                            <span className='font-semibold text-floral-700'>
                              ${variant.price}
                            </span>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <button
                        onClick={() => selectProduct(product)}
                        className='w-full bg-floral-600 text-white py-2 px-4 rounded hover:bg-floral-700 transition-colors'
                      >
                        Add - ${product.variants[0]?.price || '0.00'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── Forest layout (New order, Items step) ──────────────────────────────────
  // Each item is a bordered block with a toolbar; product / price / qty /
  // subtotal on one line, product type and instructions beneath. The Formula
  // calculator opens from the step header for the last custom item, and from
  // an item's toolbar when there are several custom items to choose from.
  if (layout === 'forest') {
    const field = (error, extra = '') => `input-field sm:text-base${error ? ' input-error' : ''}${extra ? ` ${extra}` : ''}`;
    const customIndexes = items.map((item, i) => (item.isProduct ? null : i)).filter((i) => i !== null);
    const formulaTarget = customIndexes.length ? customIndexes[customIndexes.length - 1] : null;
    const toolbarButton = 'h-6 rounded-[5px] px-2 text-xs text-gray-500 transition-colors duration-[120ms] hover:bg-gray-200 hover:text-gray-900';

    return (
      <OrderFormSection
        step={step}
        title='Items'
        data-section='items'
        aside={(
          <button
            type='button'
            onClick={() => formulaTarget !== null && setFormulaItemIndex(formulaTarget)}
            disabled={formulaTarget === null}
            className='btn-secondary btn-sm'
            title={formulaTarget === null ? 'The Formula calculator works on custom items' : `Formula calculator for item ${formulaTarget + 1}`}
          >
            Formula calculator
          </button>
        )}
        bodyClassName='flex flex-col gap-3.5'
      >
        {items.map((item, index) => {
          const hasItemError = ['name', 'price', 'quantity'].some(
            (f) => validationErrors[`items.${index}.${f}`]
          );
          const priceOverridden = item.isProduct && item.originalPrice &&
            parseFloat(item.price) !== parseFloat(item.originalPrice);
          const nameLength = (item.name || '').length;
          return (
            <div
              key={item.id || index}
              className={`subcard overflow-hidden rounded-md border ${hasItemError ? 'border-danger' : 'border-gray-200'}`}
            >
              {/* Toolbar */}
              <div className='flex items-center gap-2.5 border-b border-gray-200 bg-gray-50 px-3.5 py-2 text-xs text-gray-500'>
                <span className='subcard-title font-semibold text-gray-600'>Item {index + 1}</span>
                {item.isProduct && (
                  <span className='inline-flex h-5 items-center rounded-md bg-floral-100 px-2 text-[11px] font-semibold text-floral-900'>
                    Product
                  </span>
                )}
                {item.isShopifyProduct && (
                  <span className='inline-flex h-5 items-center rounded-md bg-floral-100 px-2 text-[11px] font-semibold text-floral-900'>
                    Shopify
                  </span>
                )}
                <span className='flex-1' />
                {!item.isProduct && customIndexes.length > 1 && (
                  <button type='button' onClick={() => setFormulaItemIndex(index)} className={toolbarButton}>
                    Formula
                  </button>
                )}
                <button
                  type='button'
                  onClick={() => duplicateItem(index)}
                  className={toolbarButton}
                  tabIndex={tabIndex + 100 + index * 10}
                >
                  Duplicate
                </button>
                <button
                  type='button'
                  onClick={() => clearItem(index)}
                  className={toolbarButton}
                  tabIndex={tabIndex + 100 + index * 10 + 1}
                >
                  Clear
                </button>
                {items.length > 1 && (
                  <button
                    type='button'
                    onClick={() => removeItem(index)}
                    className={`${toolbarButton} hover:!bg-danger-bg hover:!text-danger-fg`}
                    tabIndex={tabIndex + 100 + index * 10 + 2}
                  >
                    Remove
                  </button>
                )}
              </div>

              <div className='grid grid-cols-1 gap-x-3.5 gap-y-3 p-3.5 md:grid-cols-[minmax(0,3fr)_110px_120px_110px]'>
                {/* Product or description (product autocomplete) */}
                <div className='min-w-0' data-field={`items.${index}.name`}>
                  <span className='field-label input-label'>
                    Product or description <span className='input-required'>*</span>
                  </span>
                  <div className='flex items-center gap-2.5'>
                    {(item.image_url || item.image) && (
                      <img
                        src={item.image_url || item.image}
                        alt=''
                        className='h-ctl w-[var(--control-h)] shrink-0 rounded-md border border-gray-200 object-cover'
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    <div className='min-w-0 flex-1'>
                      <ProductAutocomplete
                        value={item.name}
                        onChange={(value) => handleItemChange(index, 'name', value)}
                        onProductSelect={(product) => handleProductSelect(index, product)}
                        onBlurExtract={(extraText) => handleBlurExtract(index, extraText)}
                        selectedProductTitle={item.isProduct ? item.selectedProductTitle : null}
                        placeholder='Search products or type a custom item'
                        maxLength={255}
                        className={field(getFieldError(index, 'name'))}
                      />
                    </div>
                  </div>
                </div>

                {/* Price */}
                <div>
                  <label className='field-label input-label' htmlFor={`item-${item.id || index}-price`}>
                    Price <span className='input-required'>*</span>
                  </label>
                  <input
                    id={`item-${item.id || index}-price`}
                    type='text'
                    inputMode='decimal'
                    value={item.price || ''}
                    onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                    onBlur={(e) => {
                      // Format on blur for better UX
                      if (e.target.value) {
                        const formatted = parseCurrency(e.target.value);
                        if (formatted !== e.target.value) {
                          handleItemChange(index, 'price', formatted);
                        }
                      }
                    }}
                    data-field={`items.${index}.price`}
                    className={field(getFieldError(index, 'price'), `text-right tabular-nums${priceOverridden ? ' !border-warn-fg' : ''}`)}
                    placeholder='0.00'
                    tabIndex={tabIndex + 100 + index * 10 + 3}
                  />
                </div>

                {/* Quantity stepper */}
                <div>
                  <label className='field-label input-label' htmlFor={`item-${item.id || index}-qty`}>Qty</label>
                  <div className={`flex h-ctl overflow-hidden rounded-md border ${getFieldError(index, 'quantity') ? 'border-danger' : 'border-gray-300'} bg-white`}>
                    <button
                      type='button'
                      onClick={() => handleItemChange(index, 'quantity', Math.max(1, item.quantity - 1))}
                      disabled={!(item.quantity > 1)}
                      className='w-9 shrink-0 bg-gray-50 text-base text-gray-900 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:text-gray-400 disabled:hover:bg-gray-50'
                      aria-label='One fewer'
                      tabIndex={-1}
                    >
                      −
                    </button>
                    <input
                      id={`item-${item.id || index}-qty`}
                      type='number'
                      min='1'
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
                      className='w-full min-w-0 border-0 bg-transparent p-0 text-center text-base font-semibold tabular-nums text-gray-900 focus:ring-0 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'
                      tabIndex={tabIndex + 100 + index * 10 + 4}
                    />
                    <button
                      type='button'
                      onClick={() => handleItemChange(index, 'quantity', item.quantity + 1)}
                      className='w-9 shrink-0 bg-gray-50 text-base text-gray-900 transition-colors hover:bg-gray-200'
                      aria-label='One more'
                      tabIndex={-1}
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Subtotal (read-only) */}
                <div>
                  <label className='field-label input-label' htmlFor={`item-${item.id || index}-subtotal`}>Subtotal</label>
                  <input
                    id={`item-${item.id || index}-subtotal`}
                    type='text'
                    readOnly
                    tabIndex={-1}
                    value={formatCurrency((parseFloat(item.price) || 0) * (parseInt(item.quantity) || 0))}
                    className='input-field text-right tabular-nums sm:text-base'
                  />
                </div>

                {/* Messages that belong to this line */}
                {(getFieldError(index, 'name') || nameLength >= 230 || getFieldError(index, 'price') || priceOverridden) && (
                  <div className='-mt-1.5 flex flex-wrap items-start justify-between gap-x-4 gap-y-1 md:col-span-4'>
                    <span>
                      {getFieldError(index, 'name') ? (
                        <span className='text-xs text-danger-fg'>{getFieldError(index, 'name')}</span>
                      ) : nameLength >= 230 ? (
                        <span className='text-xs text-warn-fg'>
                          Approaching limit — use Special instructions below for additional details
                        </span>
                      ) : null}
                      {nameLength >= 230 && (
                        <span className={`ml-2 text-xs tabular-nums ${nameLength >= 255 ? 'font-semibold text-danger-fg' : 'text-warn-fg'}`}>
                          {nameLength}/255
                        </span>
                      )}
                    </span>
                    <span className='text-right'>
                      {getFieldError(index, 'price') && (
                        <span className='block text-xs text-danger-fg'>{getFieldError(index, 'price')}</span>
                      )}
                      {priceOverridden && (
                        <span className='block text-xs text-gray-500'>
                          <span className='font-semibold text-warn-fg'>Override</span> · Original:{' '}
                          <span className='line-through'>{formatCurrency(parseFloat(item.originalPrice))}</span>
                        </span>
                      )}
                    </span>
                  </div>
                )}

                {/* Variant, ticket and stock notices */}
                {item.variants && item.variants.length > 1 && (
                  <div className='md:col-span-4'>
                    <label className='field-label input-label' htmlFor={`item-${item.id || index}-variant`}>
                      Variant <span className='input-required'>*</span>
                      {item.needsVariantSelection && (
                        <span className='ml-2 text-xs font-semibold text-danger-fg'>Required</span>
                      )}
                    </label>
                    <select
                      id={`item-${item.id || index}-variant`}
                      value={item.selectedVariant?.id || ''}
                      onChange={(e) => {
                        const variant = item.variants.find(v => v.id === e.target.value);
                        if (variant) {
                          handleVariantSelect(index, variant);
                        }
                      }}
                      className={field(item.needsVariantSelection, 'py-0')}
                      required
                    >
                      <option value=''>-- Select {item.variants[0]?.title?.includes('Color') ? 'Color' : 'Option'} --</option>
                      {item.variants.map((variant) => {
                        const left = isTicketItem(item) ? seatsLeft(variant) : null;
                        const seats = left === null ? '' : left <= 0 ? ' — FULL' : ` — ${left} left`;
                        return (
                          <option key={variant.id} value={variant.id}>
                            {variant.title} - ${parseFloat(variant.price || 0).toFixed(2)}{seats}
                          </option>
                        );
                      })}
                    </select>
                    {item.needsVariantSelection && (
                      <p className='input-error-text'>Please select a variant before submitting</p>
                    )}
                  </div>
                )}

                {/* Ticket oversell warning: staff may sell past capacity on purpose */}
                {isTicketItem(item) && (() => {
                  const left = seatsLeft(item.selectedVariant);
                  const qty = parseInt(item.quantity) || 0;
                  if (left === null || qty <= left) return null;
                  const over = qty - Math.max(left, 0);
                  return (
                    <div className='banner banner-warn items-start font-normal md:col-span-4'>
                      <AlertTriangleIcon size={16} className='mt-px shrink-0' />
                      <span>
                        {left <= 0 ? 'This time slot is full.' : `Only ${left} seat${left !== 1 ? 's' : ''} left in this time slot.`}
                        {' '}Selling this oversells it by {over}. The website will keep showing it sold out.
                      </span>
                    </div>
                  );
                })()}

                {showLowStockWarnings && !isTicketItem(item) && item.inventoryTracked && item.inventoryQuantity !== null && item.inventoryQuantity < 5 && (
                  <div className='banner banner-warn items-start font-normal md:col-span-4'>
                    <AlertTriangleIcon size={16} className='mt-px shrink-0' />
                    <span>
                      <strong className='font-semibold'>Low stock</strong> · Only {item.inventoryQuantity} unit{item.inventoryQuantity !== 1 ? 's' : ''} remaining in inventory
                    </span>
                  </div>
                )}

                {/* Price note (catalog products only, when the price was changed) */}
                {priceOverridden && (
                  <div className='md:col-span-4'>
                    <label className='field-label input-label' htmlFor={`item-${item.id || index}-price-note`}>
                      Price note <span className='field-hint font-normal text-gray-500'>· optional, reason for the price change</span>
                    </label>
                    <input
                      id={`item-${item.id || index}-price-note`}
                      type='text'
                      value={item.priceNote || ''}
                      onChange={(e) => handleItemChange(index, 'priceNote', e.target.value)}
                      className={field(false)}
                      placeholder='e.g., 10% loyalty discount, upsell with premium stems...'
                      tabIndex={tabIndex + 100 + index * 10 + 5}
                    />
                  </div>
                )}

                {/* Product type (custom items) + special instructions */}
                {!item.isProduct && (
                  <div className='min-w-0'>
                    <label className='field-label input-label' htmlFor={`item-${item.id || index}-type`}>
                      Product type
                      {!manuallySetTypes[item.id] && matchProductType(item.name, productTypeOptions) && (
                        <span className='field-hint ml-1 font-normal text-gray-500'>· auto-detected</span>
                      )}
                    </label>
                    <select
                      id={`item-${item.id || index}-type`}
                      value={item.productType || DEFAULT_PRODUCT_TYPE}
                      onChange={(e) => handleItemChange(index, 'productType', e.target.value)}
                      className={field(false, 'py-0')}
                      tabIndex={tabIndex + 100 + index * 10 + 5}
                    >
                      {productTypeOptions.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className={item.isProduct ? 'md:col-span-4' : 'md:col-span-3'}>
                  <label className='field-label input-label' htmlFor={`item-${item.id || index}-instructions`}>
                    Special instructions <span className='field-hint font-normal text-gray-500'>· optional</span>
                  </label>
                  <textarea
                    id={`item-${item.id || index}-instructions`}
                    value={item.instructions || ''}
                    onChange={(e) => handleItemChange(index, 'instructions', e.target.value)}
                    rows={1}
                    className={field(false, 'min-h-[var(--control-h)] resize-y py-[5px]')}
                    placeholder='Color preferences, no lilies, keep it low…'
                    tabIndex={tabIndex + 100 + index * 10 + 7}
                  />
                </div>

                {/* Color preferences (custom items) — accordion */}
                {!item.isProduct && (
                  <div className='md:col-span-4'>
                    <button
                      type='button'
                      onClick={() => toggleColorPrefs(item.id)}
                      className='inline-flex items-center gap-1.5 text-[13px] font-medium text-gray-600 hover:text-gray-900'
                      aria-expanded={!!expandedColorPrefs[item.id]}
                      tabIndex={tabIndex + 100 + index * 10 + 6}
                    >
                      <ChevronRightIcon size={14} className={`transition-transform ${expandedColorPrefs[item.id] ? 'rotate-90' : ''}`} />
                      Color preferences
                      {item.colors && item.colors.length > 0 && (
                        <span className='field-hint font-normal text-gray-500'>· {item.colors.length} selected</span>
                      )}
                    </button>
                    {expandedColorPrefs[item.id] && (
                      <div className='mt-2.5 grid grid-cols-2 gap-2 pl-5 md:grid-cols-4'>
                        {COLOR_OPTIONS.map((color) => (
                          <label key={color} className='flex cursor-pointer items-center gap-2 text-sm text-gray-900'>
                            <input
                              type='checkbox'
                              checked={(item.colors || []).includes(color)}
                              onChange={(e) => handleColorChange(index, color, e.target.checked)}
                              className='h-4 w-4 rounded border-gray-300 text-floral-700 focus:ring-floral-500'
                              tabIndex={tabIndex + 100 + index * 10 + 7}
                            />
                            {color}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        <button
          type='button'
          onClick={() => addItem('custom')}
          className='inline-flex h-ctl items-center gap-2 self-start rounded-md border border-dashed border-gray-300 px-3.5 text-sm font-medium text-gray-600 transition-colors duration-[120ms] hover:border-floral-700 hover:text-floral-700'
          tabIndex={tabIndex + 50}
        >
          <PlusIcon size={14} strokeWidth={2.5} />
          Add another item
        </button>

        <ProductModal />

        {/* Formula Calculator — inline builder for a custom arrangement item */}
        {formulaItemIndex !== null && (
          <div
            className='fixed inset-0 z-50 bg-gray-900/50 [html[data-scheme=dark]:not([data-appearance=fable])_&]:bg-black/60 flex items-center justify-center p-4'
            onClick={(e) => {
              if (e.target === e.currentTarget) setFormulaItemIndex(null);
            }}
          >
            <div className='bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[92vh] overflow-y-auto'>
              <FormulaBuilder
                existingItem={items[formulaItemIndex]}
                onClose={() => setFormulaItemIndex(null)}
                onCommit={(formula) => applyFormulaToItem(formulaItemIndex, formula)}
              />
            </div>
          </div>
        )}
      </OrderFormSection>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Section Header */}
      {/* <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-floral-800">Order Items</h3>
      </div> */}

      {/* Items List */}
      <div className='space-y-4'>
        {items.map((item, index) => {
          const hasItemError = ['name', 'price', 'quantity'].some(
            f => validationErrors[`items.${index}.${f}`]
          );
          return (
          <div
            key={item.id || index}
            className={`subcard rounded-lg p-4 border-l-4 ${
              hasItemError
                ? 'border-red-400 border-y border-r border-y-red-200 border-r-red-200 bg-red-50/30'
                : 'border border-floral-200'
            }`}
          >
            {/* Item Header */}
            <div className='flex justify-between items-start mb-4'>
              <div className='flex items-center space-x-2'>
                <h4 className='subcard-title font-medium text-floral-800'>
                  Item {index + 1}
                  {item.isProduct && (
                    <span className='ml-2 text-xs bg-floral-100 text-floral-800 px-2 py-1 rounded'>
                      Product
                    </span>
                  )}
                </h4>
              </div>

              <div className='flex items-center space-x-2'>
                {!item.isProduct && (
                  <button
                    type='button'
                    onClick={() => setFormulaItemIndex(index)}
                    className='flex items-center gap-1.5 px-2 py-1 text-floral-600 hover:text-floral-800 hover:bg-floral-100 rounded text-sm font-medium'
                    title='Formula Calculator'
                  >
                    <svg
                      className='w-4 h-4 shrink-0'
                      fill='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <circle cx='12' cy='7' r='3.5' />
                      <circle cx='16.33' cy='9.5' r='3.5' />
                      <circle cx='16.33' cy='14.5' r='3.5' />
                      <circle cx='12' cy='17' r='3.5' />
                      <circle cx='7.67' cy='14.5' r='3.5' />
                      <circle cx='7.67' cy='9.5' r='3.5' />
                      <circle cx='12' cy='12' r='2' fillOpacity='0.5' />
                    </svg>
                    <span className='hidden sm:inline whitespace-nowrap'>
                      Formula Calculator
                    </span>
                  </button>
                )}

                <button
                  type='button'
                  onClick={() => duplicateItem(index)}
                  className='p-1 text-floral-600 hover:text-floral-800 hover:bg-floral-100 rounded'
                  title='Duplicate item'
                  tabIndex={tabIndex + 100 + index * 10}
                >
                  <svg
                    className='w-4 h-4'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                      d='M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z'
                    />
                  </svg>
                </button>

                <button
                  type='button'
                  onClick={() => clearItem(index)}
                  className='p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded'
                  title='Clear item'
                  tabIndex={tabIndex + 100 + index * 10 + 1}
                >
                  <svg
                    className='w-4 h-4'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                      d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
                    />
                  </svg>
                </button>

                {items.length > 1 && (
                  <button
                    type='button'
                    onClick={() => removeItem(index)}
                    className='p-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded'
                    title='Remove item'
                    tabIndex={tabIndex + 100 + index * 10 + 2}
                  >
                    <svg
                      className='w-4 h-4'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='2'
                        d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Product Image */}
            {(item.image_url || item.image) && (
              <div className='mb-4'>
                <img
                  src={item.image_url || item.image}
                  alt={item.name || 'Product'}
                  className='w-24 h-24 object-cover rounded-lg border border-gray-200 shadow-sm'
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}

            {/* Item Fields */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-4'>
              {/* Name/Description with Product Autocomplete */}
              <div className='md:col-span-2' data-field={`items.${index}.name`}>
                <label className='field-label block text-sm font-medium text-floral-700 mb-2'>
                  Item Name/Description *
                  {item.isShopifyProduct && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-floral-100 text-floral-800">
                      Shopify
                    </span>
                  )}
                </label>
                <ProductAutocomplete
                  value={item.name}
                  onChange={(value) => handleItemChange(index, 'name', value)}
                  onProductSelect={(product) => handleProductSelect(index, product)}
                  onBlurExtract={(extraText) => handleBlurExtract(index, extraText)}
                  selectedProductTitle={item.isProduct ? item.selectedProductTitle : null}
                  placeholder="Search products or type custom item..."
                  maxLength={255}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-floral-500 ${
                    getFieldError(index, 'name')
                      ? 'border-red-300 focus:border-red-500'
                      : 'border-floral-300 focus:border-floral-500'
                  }`}
                />
                <div className='flex justify-between items-center mt-1'>
                  {getFieldError(index, 'name') ? (
                    <p className='text-sm text-red-600'>
                      {getFieldError(index, 'name')}
                    </p>
                  ) : (item.name || '').length >= 230 ? (
                    <p className='text-xs text-amber-600'>
                      Approaching limit — use Special Instructions below for additional details
                    </p>
                  ) : <span />}
                  <span className={`text-xs tabular-nums ${
                    (item.name || '').length >= 255
                      ? 'text-red-600 font-semibold'
                      : (item.name || '').length >= 230
                        ? 'text-amber-600'
                        : 'text-gray-400'
                  }`}>
                    {(item.name || '').length}/255
                  </span>
                </div>
                {/* Variant Selector */}
                {item.variants && item.variants.length > 1 && (
                  <div className='mt-3'>
                    <label className='field-label block text-sm font-medium text-floral-700 mb-2'>
                      Select Variant *
                      {item.needsVariantSelection && (
                        <span className="ml-2 text-xs text-red-600 font-semibold">Required</span>
                      )}
                    </label>
                    <select
                      value={item.selectedVariant?.id || ''}
                      onChange={(e) => {
                        const variant = item.variants.find(v => v.id === e.target.value);
                        if (variant) {
                          handleVariantSelect(index, variant);
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-floral-500 ${
                        item.needsVariantSelection
                          ? 'border-red-300 focus:border-red-500 bg-red-50'
                          : 'border-floral-300 focus:border-floral-500'
                      }`}
                      required
                    >
                      <option value="">-- Select {item.variants[0]?.title?.includes('Color') ? 'Color' : 'Option'} --</option>
                      {item.variants.map((variant) => {
                        const left = isTicketItem(item) ? seatsLeft(variant) : null;
                        const seats = left === null ? '' : left <= 0 ? ' — FULL' : ` — ${left} left`;
                        return (
                          <option key={variant.id} value={variant.id}>
                            {variant.title} - ${parseFloat(variant.price || 0).toFixed(2)}{seats}
                          </option>
                        );
                      })}
                    </select>
                    {item.needsVariantSelection && (
                      <p className='mt-1 text-sm text-red-600'>
                        Please select a variant before submitting
                      </p>
                    )}
                  </div>
                )}

                {/* Ticket oversell warning: staff may sell past capacity on purpose */}
                {isTicketItem(item) && (() => {
                  const left = seatsLeft(item.selectedVariant);
                  const qty = parseInt(item.quantity) || 0;
                  if (left === null || qty <= left) return null;
                  const over = qty - Math.max(left, 0);
                  return (
                    <div className='mt-2 p-3 bg-amber-50 border border-amber-300 rounded-md flex items-start gap-2'>
                      <AlertTriangleIcon size={20} className='text-amber-700 flex-shrink-0 mt-0.5' />
                      <p className='text-sm text-amber-900'>
                        {left <= 0 ? 'This time slot is full.' : `Only ${left} seat${left !== 1 ? 's' : ''} left in this time slot.`}
                        {' '}Selling this oversells it by {over}. The website will keep showing it sold out.
                      </p>
                    </div>
                  );
                })()}

                {/* Low Stock Warning */}
                {showLowStockWarnings && !isTicketItem(item) && item.inventoryTracked && item.inventoryQuantity !== null && item.inventoryQuantity < 5 && (
                  <div className='mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md flex items-start gap-2'>
                    <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div className='flex-1'>
                      <p className='text-sm font-medium text-yellow-800'>Low Stock Warning</p>
                      <p className='text-sm text-yellow-700'>Only {item.inventoryQuantity} unit{item.inventoryQuantity !== 1 ? 's' : ''} remaining in inventory</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Price, Quantity, Subtotal - All on one line */}
              <div className='md:col-span-2 grid grid-cols-3 gap-3'>
                {/* Price */}
                <div>
                  <label className='field-label block text-sm font-medium text-floral-700 mb-2'>
                    Price *
                    {item.isProduct && item.originalPrice &&
                     parseFloat(item.price) !== parseFloat(item.originalPrice) && (
                      <span className='ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800'>
                        Override
                      </span>
                    )}
                  </label>
                  <input
                    type='text'
                    value={item.price || ''}
                    onChange={(e) =>
                      handleItemChange(index, 'price', e.target.value)
                    }
                    onBlur={(e) => {
                      // Format on blur for better UX
                      if (e.target.value) {
                        const formatted = parseCurrency(e.target.value);
                        if (formatted !== e.target.value) {
                          handleItemChange(index, 'price', formatted);
                        }
                      }
                    }}
                    data-field={`items.${index}.price`}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                      getFieldError(index, 'price')
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                        : item.isProduct && item.originalPrice && parseFloat(item.price) !== parseFloat(item.originalPrice)
                          ? 'border-amber-300 focus:border-amber-500 focus:ring-amber-500'
                          : 'border-floral-300 focus:border-floral-500 focus:ring-floral-500'
                    }`}
                    placeholder='150.00'
                    tabIndex={tabIndex + 100 + index * 10 + 3}
                  />
                  {item.isProduct && item.originalPrice &&
                   parseFloat(item.price) !== parseFloat(item.originalPrice) && (
                    <p className='mt-1 text-xs text-gray-500'>
                      Original: <span className='line-through'>{formatCurrency(parseFloat(item.originalPrice))}</span>
                    </p>
                  )}
                  {getFieldError(index, 'price') && (
                    <p className='mt-1 text-sm text-red-600'>
                      {getFieldError(index, 'price')}
                    </p>
                  )}
                </div>

                {/* Quantity with +/- buttons */}
                <div>
                  <label className='field-label block text-sm font-medium text-floral-700 mb-2'>
                    Qty
                  </label>
                  <div className='relative flex items-center'>
                    {item.quantity > 1 && (
                      <button
                        type='button'
                        onClick={() =>
                          handleItemChange(index, 'quantity', Math.max(1, item.quantity - 1))
                        }
                        className='absolute left-0 h-full px-2 text-floral-700 hover:text-floral-900 focus:outline-none z-10'
                        tabIndex={-1}
                      >
                        <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M20 12H4' />
                        </svg>
                      </button>
                    )}
                    <input
                      type='number'
                      min='1'
                      value={item.quantity}
                      onChange={(e) =>
                        handleItemChange(
                          index,
                          'quantity',
                          parseInt(e.target.value) || 1
                        )
                      }
                      className='w-full px-3 py-2 border border-floral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-floral-500 focus:border-floral-500 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
                      style={{ paddingLeft: item.quantity > 1 ? '2rem' : '0.75rem', paddingRight: '2rem' }}
                      tabIndex={tabIndex + 100 + index * 10 + 4}
                    />
                    <button
                      type='button'
                      onClick={() =>
                        handleItemChange(index, 'quantity', item.quantity + 1)
                      }
                      className='absolute right-0 h-full px-2 text-floral-700 hover:text-floral-900 focus:outline-none z-10'
                      tabIndex={-1}
                    >
                      <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M12 4v16m8-8H4' />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Subtotal Display */}
                <div>
                  <label className='field-label block text-sm font-medium text-floral-700 mb-2'>
                    Subtotal
                  </label>
                  <div className='px-3 py-2 bg-floral-50 border border-floral-200 rounded-md text-floral-800 font-semibold'>
                    {formatCurrency(
                      (parseFloat(item.price) || 0) *
                        (parseInt(item.quantity) || 0)
                    )}
                  </div>
                </div>
              </div>

              {/* Price Override Note (Shopify products only, when price differs) */}
              {item.isProduct && item.originalPrice &&
               parseFloat(item.price) !== parseFloat(item.originalPrice) && (
                <div className='md:col-span-2'>
                  <label className='block text-sm font-medium text-amber-700 mb-1'>
                    Price Note
                    <span className='text-xs font-normal text-gray-500 ml-1'>(optional - reason for price change)</span>
                  </label>
                  <input
                    type='text'
                    value={item.priceNote || ''}
                    onChange={(e) => handleItemChange(index, 'priceNote', e.target.value)}
                    className='w-full px-3 py-2 border border-amber-200 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 text-sm'
                    placeholder='e.g., 10% loyalty discount, upsell with premium stems...'
                    tabIndex={tabIndex + 100 + index * 10 + 5}
                  />
                </div>
              )}
            </div>

            {/* Product Type (Custom Items Only) */}
            {!item.isProduct && (
              <div className='mb-4'>
                <label className='field-label block text-sm font-medium text-floral-700 mb-2'>
                  Product Type
                  {!manuallySetTypes[item.id] && matchProductType(item.name, productTypeOptions) && (
                    <span className='field-hint ml-2 text-xs text-floral-700 font-normal'>
                      (auto-detected)
                    </span>
                  )}
                </label>
                <select
                  value={item.productType || DEFAULT_PRODUCT_TYPE}
                  onChange={(e) => handleItemChange(index, 'productType', e.target.value)}
                  className='w-full px-3 py-2 border border-floral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-floral-500 focus:border-floral-500'
                  tabIndex={tabIndex + 100 + index * 10 + 5}
                >
                  {productTypeOptions.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Color Preferences (Custom Items Only) - Accordion */}
            {!item.isProduct && (
              <div className='mb-4'>
                <button
                  type='button'
                  onClick={() => toggleColorPrefs(item.id)}
                  className='flex items-center justify-between w-full text-sm font-medium text-floral-700 hover:text-floral-800 focus:outline-none'
                  tabIndex={tabIndex + 100 + index * 10 + 6}
                >
                  <span className='flex items-center'>
                    <svg
                      className={`w-4 h-4 mr-2 transition-transform ${expandedColorPrefs[item.id] ? 'rotate-90' : ''}`}
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M9 5l7 7-7 7' />
                    </svg>
                    Color Preferences
                    {item.colors && item.colors.length > 0 && (
                      <span className='field-hint ml-2 text-xs text-floral-600 font-normal'>
                        ({item.colors.length} selected)
                      </span>
                    )}
                  </span>
                </button>

                {expandedColorPrefs[item.id] && (
                  <div className='mt-3 pl-6 grid grid-cols-2 md:grid-cols-4 gap-2'>
                    {COLOR_OPTIONS.map((color) => (
                      <label
                        key={color}
                        className='flex items-center text-sm cursor-pointer'
                      >
                        <input
                          type='checkbox'
                          checked={(item.colors || []).includes(color)}
                          onChange={(e) =>
                            handleColorChange(index, color, e.target.checked)
                          }
                          className='mr-2 h-4 w-4 text-floral-600 focus:ring-floral-500 border-floral-300 rounded'
                          tabIndex={tabIndex + 100 + index * 10 + 7}
                        />
                        {color}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Special Instructions */}
            <div>
              <label className='field-label block text-sm font-medium text-floral-700 mb-2'>
                Special Instructions
              </label>
              <textarea
                value={item.instructions || ''}
                onChange={(e) =>
                  handleItemChange(index, 'instructions', e.target.value)
                }
                rows='2'
                className='w-full px-3 py-2 border border-floral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-floral-500 focus:border-floral-500'
                placeholder='Any special requests or notes for this item...'
                tabIndex={tabIndex + 100 + index * 10 + 7}
              />
            </div>
          </div>
          );
        })}
      </div>

      {/* Add Item Buttons - Centered */}
      <div className='flex justify-center items-center space-x-4 py-4'>
        <button
          type='button'
          onClick={() => addItem('custom')}
          className='px-4 py-2 bg-floral-600 text-white text-sm rounded-md hover:bg-floral-700 transition-colors flex items-center'
          tabIndex={tabIndex + 50}
        >
          <svg
            className='w-4 h-4 mr-2'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              d='M12 4v16m8-8H4'
            />
          </svg>
          Add Item
        </button>
      </div>

      {/* Order Total — omitted when the page shows its own summary (Fable's
          rail), so there is one total on screen, not two. */}
      {showTotals && (
      <div className='bg-floral-50 border border-floral-200 rounded-lg p-4'>
        <div className='space-y-3'>
          <div className='flex justify-between items-center'>
            <div>
              <h4 className='text-lg font-semibold text-floral-800'>
                Order Total
              </h4>
              <p className='field-hint text-sm text-floral-600'>
                {items.length} item{items.length !== 1 ? 's' : ''}
              </p>
            </div>
            <div className='text-right'>
              <div className='text-2xl font-bold text-floral-800'>
                {(() => {
                  const subtotal = calculateTotal() + deliveryFee + priorityTimeFee;
                  const tax = orderTax();
                  return tax == null ? taxNotYet() : formatCurrency(subtotal + tax);
                })()}
              </div>
              <p className='field-hint text-xs text-floral-600'>{isTaxExempt ? 'Tax exempt' : 'Tax included'}</p>
            </div>
          </div>

          {/* Breakdown */}
          <div className='border-t border-floral-200 pt-3 space-y-1'>
            <div className='flex justify-between text-sm text-floral-700'>
              <span>
                Subtotal ({items.length} item{items.length !== 1 ? 's' : ''})
              </span>
              <span>{formatCurrency(calculateTotal())}</span>
            </div>
            {deliveryFee > 0 && (
              <div className='flex justify-between text-sm text-floral-700'>
                <span>Delivery Fee</span>
                <span>{formatCurrency(deliveryFee)}</span>
              </div>
            )}
            {priorityTimeFee > 0 && (
              <div className='flex justify-between text-sm text-floral-700'>
                <span>Priority Time Rush</span>
                <span>{formatCurrency(priorityTimeFee)}</span>
              </div>
            )}
            <div className='flex justify-between items-center text-sm text-floral-700'>
              <span className='flex items-center gap-2'>
                <span>{isTaxExempt ? 'Tax Exempt' : (taxRateShown() == null ? 'Tax' : `Tax (${formatTaxRate(taxRateShown())})`)}</span>
                {!isTaxExempt && !stripeTaxMode && salesTaxRateNotSet && <SalesTaxNotSetChip />}
                {onTaxExemptChange && (
                  <>
                    <button
                      type='button'
                      onClick={() => onTaxExemptChange(!isTaxExempt)}
                      className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors focus:outline-none ${
                        isTaxExempt ? 'bg-floral-600' : 'bg-gray-300'
                      }`}
                      role='switch'
                      aria-checked={isTaxExempt}
                    >
                      <span
                        className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                          isTaxExempt ? 'translate-x-3.5' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                    <span className={`text-xs ${isTaxExempt ? 'text-floral-600 font-medium' : 'text-floral-500'}`}>
                      {isTaxExempt ? 'Exempt' : 'Make Exempt'}
                    </span>
                  </>
                )}
              </span>
              <span>
                {orderTax() == null ? taxNotYet() : formatCurrency(orderTax())}
              </span>
            </div>
            <div className='flex justify-between text-base font-semibold text-floral-800 pt-1 border-t border-floral-200'>
              <span>Total</span>
              <span>
                {(() => {
                  const subtotal = calculateTotal() + deliveryFee + priorityTimeFee;
                  const tax = orderTax();
                  return tax == null ? taxNotYet() : formatCurrency(subtotal + tax);
                })()}
              </span>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Tips */}
      <div className='pt-4 border-t border-floral-100'>
        <div className='field-hint text-sm text-floral-600'>
          💡 Tip: Use templates for quick setup or browse products from your catalog
        </div>
      </div>

      <ProductModal />

      {/* Formula Calculator — inline builder for a custom arrangement item */}
      {formulaItemIndex !== null && (
        <div
          className='fixed inset-0 z-50 bg-gray-900/50 [html[data-scheme=dark]:not([data-appearance=fable])_&]:bg-black/60 flex items-center justify-center p-4'
          onClick={(e) => {
            if (e.target === e.currentTarget) setFormulaItemIndex(null);
          }}
        >
          <div className='bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[92vh] overflow-y-auto'>
            <FormulaBuilder
              existingItem={items[formulaItemIndex]}
              onClose={() => setFormulaItemIndex(null)}
              onCommit={(formula) => applyFormulaToItem(formulaItemIndex, formula)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemsSection;
